import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Stars = ({ count }) => (
  <div className="rev-stars" aria-label={`${count} étoiles sur 5`}>
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={12}
        fill={i < count ? '#f5a623' : 'none'}
        stroke={i < count ? '#f5a623' : 'rgba(255,255,255,0.2)'}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const Avatar = ({ name, photoUrl }) => {
  const [err, setErr] = useState(false);
  const initial = name ? name.charAt(0).toUpperCase() : '?';
  if (photoUrl && !err) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className="rev-avatar rev-photo"
        onError={() => setErr(true)}
      />
    );
  }
  return <div className="rev-avatar">{initial}</div>;
};

export const GoogleReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(null);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mesure pour boucle seamless
  const trackRef = useRef(null);
  const [scrollDist, setScrollDist] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/places/reviews`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        setReviews(data.reviews || []);
        setRating(data.rating);
        setTotal(data.user_ratings_total);
      } catch (_) {
        // silent fail
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Après rendu des cartes, mesure la hauteur exacte de la première moitié
  useLayoutEffect(() => {
    if (!trackRef.current || reviews.length === 0) return;
    const measure = () => {
      if (!trackRef.current) return;
      // scrollHeight = hauteur totale des 2 séries
      // On veut scroller exactement la hauteur de la 1ère série
      const half = trackRef.current.scrollHeight / 2;
      setScrollDist(half);
    };
    // Attendre que le layout soit stabilisé
    requestAnimationFrame(() => requestAnimationFrame(measure));
  }, [reviews]);

  if (loading || reviews.length === 0) return null;

  const displayRating = rating ? rating.toFixed(1).replace('.', ',') : '—';
  const doubled = [...reviews, ...reviews];

  // Vitesse : ~35px/s, min 20s
  const duration = scrollDist ? Math.max(20, Math.round(scrollDist / 35)) : 30;

  return (
    <div className="rev-sidebar">
      <div className="rev-sidebar-header">
        <span className="rev-sidebar-title">Avis clients</span>
        <span className="rev-sidebar-meta">★ {displayRating} · {total} avis</span>
      </div>

      <div className="rev-sidebar-viewport">
        <div
          ref={trackRef}
          className="rev-sidebar-track"
          style={scrollDist ? {
            '--scroll-dist': `-${scrollDist}px`,
            animationDuration: `${duration}s`,
          } : {
            animationPlayState: 'paused',
          }}
        >
          {doubled.map((review, i) => (
            <article key={i} className="rev-sidebar-card">
              {review.text?.trim() && (
                <p className="rev-sidebar-text">"{review.text}"</p>
              )}
              <div className="rev-sidebar-author">
                <Avatar name={review.author_name} photoUrl={review.profile_photo_url} />
                <div>
                  <span className="rev-sidebar-name">{review.author_name}</span>
                  <div className="rev-sidebar-bottom">
                    <Stars count={review.rating} />
                    <span className="rev-sidebar-date">{review.relative_time_description}</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};
